/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Geometry as GeoJSONGeometry } from 'geojson';
import { Collection } from 'ol';
import type { Feature, Map } from 'ol';
import { noModifierKeys } from 'ol/events/condition';
import GeoJSON from 'ol/format/GeoJSON';
import type { Geometry } from 'ol/geom';
import Translate from 'ol/interaction/Translate';
import type { TranslateEvent } from 'ol/interaction/Translate';
import { useEffect, useRef } from 'react';
import type { MovedFeature } from './types';
import { roundCoordinates } from '../draw/mapDrawUtils';

export const getOlFeatureKey = (feature?: Feature): string | undefined => {
  const id = feature?.getId();
  if (id !== undefined && id !== null) {
    return String(id);
  }
  const originalId = feature?.get('originalId') as unknown;
  return typeof originalId === 'string' ? originalId : undefined;
};

export const isDefaultMovable = (feature: Feature): boolean =>
  feature.get('isSelected') === true && feature.get('isReadonly') !== true;

export const toMovedFeatures = (
  olFeatures: Feature<Geometry>[],
  projection: string,
): MovedFeature[] => {
  const formatter = new GeoJSON({ featureProjection: projection });
  return olFeatures.reduce<MovedFeature[]>((acc, olFeature) => {
    const featureId = getOlFeatureKey(olFeature);
    const geometry = olFeature.getGeometry();
    if (!featureId || !geometry) {
      return acc;
    }
    const geoJSONGeometry = formatter.writeGeometryObject(
      geometry,
    ) as GeoJSONGeometry;
    if ('coordinates' in geoJSONGeometry) {
      geoJSONGeometry.coordinates = roundCoordinates(
        geoJSONGeometry.coordinates,
      );
    }
    acc.push({ featureId, geometry: geoJSONGeometry });
    return acc;
  }, []);
};

export interface CreateMoveFeaturesOptions {
  features: Feature[];
  isMovable: (feature: Feature) => boolean;
  onMoveEnd: (features: Feature[]) => void;
  onMoveStart?: () => void;
}

export interface MoveFeaturesInteraction {
  translate: Translate;
  dispose: () => void;
}

export const createMoveFeatures = ({
  features,
  isMovable,
  onMoveEnd,
  onMoveStart,
}: CreateMoveFeaturesOptions): MoveFeaturesInteraction | undefined => {
  const movableFeatures = features.filter(isMovable);

  if (movableFeatures.length === 0) {
    return undefined;
  }

  const collection = new Collection(movableFeatures);

  const translate = new Translate({
    features: collection,
    // Cmd/Ctrl must fall through to the box select and shift to the selection toggle.
    condition: noModifierKeys,
    hitTolerance: 5,
  });

  translate.on('translatestart', () => {
    onMoveStart?.();
  });

  translate.on('translateend', (event: TranslateEvent) => {
    const [startX, startY] = event.startCoordinate;
    const [endX, endY] = event.coordinate;

    if (startX === endX && startY === endY) {
      onMoveEnd([]);
      return;
    }

    onMoveEnd(event.features.getArray() as Feature[]);
  });

  return { translate, dispose: (): void => collection.clear() };
};

export interface UseMoveFeaturesOptions {
  map?: Map | null;
  features: Feature[];
  active: boolean;
  isMovable?: (feature: Feature) => boolean;
  onMoveEnd?: (features: Feature[]) => void;
  onMoveStart?: () => void;
}

export const useMoveFeatures = ({
  map,
  features,
  active,
  isMovable,
  onMoveEnd,
  onMoveStart,
}: UseMoveFeaturesOptions): void => {
  const isMovableRef = useRef(isMovable);
  const onMoveEndRef = useRef(onMoveEnd);
  const onMoveStartRef = useRef(onMoveStart);
  useEffect(() => {
    isMovableRef.current = isMovable;
    onMoveEndRef.current = onMoveEnd;
    onMoveStartRef.current = onMoveStart;
  });

  useEffect((): (() => void) => {
    if (!map || !active || !features.length) {
      return (): void => {};
    }

    const moveFeatures = createMoveFeatures({
      features,
      isMovable: (feature) =>
        (isMovableRef.current ?? isDefaultMovable)(feature),
      onMoveEnd: (movedFeatures) => onMoveEndRef.current?.(movedFeatures),
      onMoveStart: () => onMoveStartRef.current?.(),
    });

    if (!moveFeatures) {
      return (): void => {};
    }

    map.addInteraction(moveFeatures.translate);

    return (): void => {
      moveFeatures.dispose();
      map.removeInteraction(moveFeatures.translate);
    };
  }, [map, features, active]);
};
